<template>
    <div id="unassigned-patient-visits-hc">
        <header class="frequency-filters">
            <div class="d-block d-md-flex align-items-center flex-wrap flex-lg-nowrap p-0 m-0">
                <div class="filter small-date-picker">
                    <div>
                        <label class="inputLabel" for="dateFrom">Date From</label>
                        <DatePicker :getDate="changeDate">
                            <div class="input-group date datepickerInput">
                                <input
                                    type="text"
                                    class="form-control"
                                    name="dateFrom"
                                    placeholder="MM/DD/YYYY"
                                    :value="formatDateInput(dateFrom)"
                                    @change="handleDateInputChange"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                    </div>
                </div>
                <hr class="date-divider mt-3 me-2 m-0 d-none d-md-block" />
                <div class="filter small-date-picker">
                    <div>
                        <label for="dateTo" class="inputLabel">Date To</label>
                        <DatePicker :getDate="changeDate">
                            <div class="input-group date datepickerInput">
                                <input
                                    type="text"
                                    class="form-control"
                                    name="dateTo"
                                    placeholder="MM/DD/YYYY"
                                    :value="formatDateInput(dateTo)"
                                    @change="handleDateInputChange"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                    </div>
                </div>
                <div class="filter">
                    <label class="inputLabel" for="agency-id"> Organization </label>
                    <MultiSelect
                        label-text="Organizations"
                        :dropDownOptions="agencyMultiSelectOptions"
                        :selectedOptions="selectedAgencies"
                        v-on:updateBranches="updateBranchesbyAgency"
                    />
                </div>
                <div class="filter">
                    <label class="inputLabel" for="agency-location-id"> Branch </label>
                    <MultiSelect
                        :dropDownOptions="branchMultiSelectOptions"
                        labelText="Branches"
                        :selectedOptions="selectedBranches"
                        v-on:updateBranches="updateBranches"
                    />
                </div>
                <div class="filter">
                    <label class="inputLabel" for="client-list"> Task </label>
                    <MultiSelectFilter
                        ref="tasksFilter"
                        id="tasks-list"
                        :on-fetch="getTasksList"
                        :selected-items="selectedTasks"
                        v-on:selectedItemsUpdated="selectedItemsUpdated"
                        name="Tasks"
                        :isUnassigned="true"
                    />
                </div>
                <div class="mt-4 pt-1 action-buttons">
                    <button type="button" class="btn btn-primary me-2 ms-2 mb-1" @click="pageReset(true)">
                        Generate
                    </button>
                    <button type="button" class="btn btn-outline-primary mb-1" @click="clearFilters()">Clear</button>
                </div>
            </div>
        </header>
        <div v-if="bulkReassignEnabled" :class="`card mb-1 px-5 pb-2 pt-3 ${isAddItemRequested ? '' : 'hide-form'}`">
            <h4>Bulk Reassign</h4>
            <EmployeeBulkReassign
                :application="application"
                :agency-id="firstAgencyId"
                user-id="39fb95f3-b19e-41ea-5213-76ddfe458554"
                :selected-task-id="checkedTask()"
                :close-reassign-form="closeReassignForm"
                :page-reset="pageReset"
                :date-from="dateFrom"
                :date-to="dateTo"
                :branch-id="selectedBranches[0]"
            />
        </div>
        <main class="bg-white rounded mt-2 h-100">
            <PageLayout>
                <template #content>
                    <div class="d-flex w-100 align-items-center justify-content-between mt-2">
                        <h2 class="font-size-xl">{{ applicationName }}{{ unassignedVisitsLabel }}</h2>
                        <div class="bulk-assign-actions">
                            <button
                                type="button"
                                class="btn btn-primary ms-lg-2 mb-1"
                                :disabled="!hasData || isExporting"
                                @click="exportVisits()"
                            >
                                Export to Excel
                            </button>
                            <span
                                v-tooltip="{
                                    title: 'No data available to export. Apply filters or adjust your selection to enable export.'
                                }"
                                v-show="!hasData"
                            >
                                <i class="fas fa-info-circle text-primary"></i>
                            </span>
                            <button
                                type="button"
                                :disabled="!bulkReassignEnabled"
                                class="btn btn-primary mb-1"
                                @click="openReassignForm()"
                            >
                                Bulk Assign
                            </button>
                            <a
                                tabindex="0"
                                href="javascript:void(0);"
                                role="button"
                                v-custom-popover="{ content: tooltip, className: 'w-30 font-size-sm info-popup' }"
                            >
                                <i class="fas fa-info-circle text-primary"></i>
                            </a>
                        </div>
                    </div>
                    <div class="h-100 d-flex flex-column mb-3 mt-2">
                        <Pagination
                            ref="unassignedVisitsHOSPALPagination"
                            :pageChanged="pageChangedCallback"
                            :isLoading="isLoading"
                            entity="Unassigned Visits"
                            :showPageSizeOptions="true"
                            classes="pagination-footer pb-2"
                            :pageSize="pageSize"
                            v-on:updatePageSizeChanged="updatePageSizeChanged"
                        >
                            <div class="table-scroll-wrapper">
                                <table class="table table-responsive-md border border-gray">
                                    <thead>
                                        <tr class="bg-table-header">
                                            <th style="width: 50px">
                                                <div class="form-check custom-checkbox form-check-inline ms-1 me-0">
                                                    <input
                                                        type="checkbox"
                                                        class="form-check-input"
                                                        id="task-select-all"
                                                        :disabled="isLoading"
                                                        v-model="selectAll"
                                                        @change="changeAllSelection()"
                                                    />
                                                    <label class="form-check-label highlight" for="task-select-all">
                                                        <span></span>
                                                    </label>
                                                </div>
                                            </th>
                                            <th>
                                                <SortableTh
                                                    :sort-by="sortBy"
                                                    v-on:sortByUpdated="sortByUpdated"
                                                    :sort-order="sortOrder"
                                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                                    sort-string="task"
                                                    class="justify-content-start"
                                                >
                                                    Task
                                                </SortableTh>
                                            </th>
                                            <th>Organization</th>
                                            <th>
                                                <SortableTh
                                                    :sort-by="sortBy"
                                                    v-on:sortByUpdated="sortByUpdated"
                                                    :sort-order="sortOrder"
                                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                                    sort-string="branchName"
                                                    class="justify-content-start"
                                                >
                                                    Branch
                                                </SortableTh>
                                            </th>
                                            <th>
                                                <SortableTh
                                                    :sort-by="sortBy"
                                                    v-on:sortByUpdated="sortByUpdated"
                                                    :sort-order="sortOrder"
                                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                                    sort-string="visitDate"
                                                    class="justify-content-start"
                                                >
                                                    Schedule Date
                                                </SortableTh>
                                            </th>
                                            <th>Day</th>
                                            <th>Time</th>
                                            <th>
                                                <SortableTh
                                                    :sort-by="sortBy"
                                                    v-on:sortByUpdated="sortByUpdated"
                                                    :sort-order="sortOrder"
                                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                                    sort-string="taskStatus"
                                                    class="justify-content-start"
                                                >
                                                    Status
                                                </SortableTh>
                                            </th>
                                            <th>
                                                <SortableTh
                                                    :sort-by="sortBy"
                                                    v-on:sortByUpdated="sortByUpdated"
                                                    :sort-order="sortOrder"
                                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                                    sort-string="lastName"
                                                    class="justify-content-start"
                                                >
                                                    Client
                                                </SortableTh>
                                            </th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <!-- EMPTY -->
                                        <tr class="table-empty" v-if="!isLoading && !isError && !hasData">
                                            <td :colspan="columns">
                                                <h3>No Tasks Found.</h3>
                                            </td>
                                        </tr>
                                        <!-- ERROR -->
                                        <tr class="table-error" v-if="!isLoading && isError">
                                            <td :colspan="columns">
                                                <ErrorMessageHandler
                                                    title="Unassigned visit list"
                                                    v-on:try-again="pageReset()"
                                                />
                                            </td>
                                        </tr>
                                        <!-- LOADING -->
                                        <template v-if="isLoading">
                                            <tr v-for="i of loadingCount" class="table-loading" :key="i">
                                                <td v-for="i of columns" :key="i">
                                                    <span class="shimmer-loading"></span>
                                                </td>
                                            </tr>
                                        </template>
                                    </tbody>
                                    <template v-if="!isLoading && !isError && hasData">
                                        <tbody>
                                            <tr
                                                v-for="task of page.Items"
                                                :key="task.TaskId"
                                                :class="`${checkedTask().includes(task.TaskId) ? 'select-row' : ''}`"
                                            >
                                                <td>
                                                    <div
                                                        class="form-check custom-checkbox form-check-inline zindex-0 ms-1"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            class="form-check-input"
                                                            :id="`task_${task.TaskId}`"
                                                            :value="task"
                                                            v-model="selectedVisits"
                                                        />
                                                        <label class="form-check-label" :for="`task_${task.TaskId}`">
                                                            <span></span>
                                                        </label>
                                                    </div>
                                                </td>
                                                <td>
                                                    <EmptyTextHandler :text="task.VisitType"></EmptyTextHandler>
                                                </td>
                                                <td>{{ task.AccountName }}</td>
                                                <td>
                                                    <EmptyTextHandler :text="task.BranchName"></EmptyTextHandler>
                                                </td>
                                                <td>{{ formatDate(task.VisitDate) }}</td>
                                                <td>{{ moment(task.VisitDate).format("dddd") }}</td>
                                                <td>{{ moment(task.StartTime, "HH:mm:ss").format("hh:mm A") }}</td>
                                                <td>
                                                    <EmptyTextHandler :text="task.TaskStatus"></EmptyTextHandler>
                                                </td>
                                                <td>
                                                    {{ task.LastName ? task.LastName + "," : "" }}
                                                    {{ task.FirstName }}
                                                </td>
                                                <td class="text-start">
                                                    <button
                                                        type="button"
                                                        class="btn px-1 py-0 ms-1 btn-outline-primary icon-size"
                                                        data-bs-toggle="dropdown"
                                                        id="UnassignedRow__Actions${id}"
                                                    >
                                                        <i class="fas fa-ellipsis-h icon-size"></i>
                                                    </button>
                                                    <div
                                                        class="dropdown-menu"
                                                        aria-labelledby="UnassignedRow__Actions${id}"
                                                    >
                                                        <a
                                                            class="dropdown-item"
                                                            href="javascript:void(0);"
                                                            @click="scheduledVisit(task)"
                                                        >
                                                            Assign to User
                                                        </a>

                                                        <a
                                                            class="dropdown-item near-ai-button"
                                                            href="javascript:void(0);"
                                                            @click="handleFindNearByEmployees(task)"
                                                        >
                                                            Find Nearby Employees
                                                        </a>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </template>
                                </table>
                            </div>
                        </Pagination>
                    </div>
                </template>
            </PageLayout>
        </main>
    </div>
</template>

<style src="./UnassignedPatientVisitsHCHOSPAL.scss"></style>
<script src="./UnassignedPatientVisitsHCHOSPAL.ts"></script>
